/**
 * The incentive plan: a per-tier transaction bar, and whether anyone clears it.
 *
 * THE CONFIGURATION HALF is one number per tier — the annual PNRs a CSM staffing
 * that tier must book to earn the incentive — plus the one team-wide percentage
 * of base salary that the incentive pays out at. Both live on the config, both
 * are published as a version, and both are edited in one place (Team → Incentive)
 * because they are one policy, not two settings.
 *
 * THE MEASUREMENT HALF is why this file exists at all rather than being a form.
 * A threshold nobody can check is a number you typed back to yourself: the claim
 * that a higher Enterprise bar is reachable *because* those CSMs carry the
 * largest customers is exactly the kind of thing this tool should confirm or
 * refute, not restate. So each bar is shown against what the people standing
 * behind it actually book.
 *
 * TWO HONESTY RULES RUN THROUGH EVERYTHING HERE.
 *
 *   1. NINETY DAYS IS NOT A MONTH. The incentive is calculated and paid monthly
 *      (Jeff, 2026-08-29), so the bar is a monthly PNR count — while the only
 *      data the CRM records is a trailing-90-day total. The comparison therefore
 *      shows the AVERAGE MONTH across that window, which is a measurement of the
 *      window rather than a projection beyond it, and it is computed in exactly
 *      one named function. An average month is not the month a bonus is actually
 *      paid on: a strong month and a weak one inside the window average to a
 *      figure neither of them was.
 *
 *   2. UNRECORDED IS NOT ZERO. A customer with no PNR data is missing from
 *      `pnrs90` entirely, and summing an absent key as zero would understate a
 *      CSM's transactions by precisely the customers nobody has data for — while
 *      the total still looked like a measurement. Every roll-up here carries the
 *      count it could not measure, so a reader can weigh the comparison.
 */
import type { Account } from '@andavo/staffing-engine';
import type { SlotDto, StoredTierDto } from '../api';

/** The window cm-api's `pnrs90` counts. */
export const PNR_WINDOW_DAYS = 90;

/** An average calendar month, so the window converts to whole months honestly. */
const DAYS_PER_MONTH = 365 / 12;

/**
 * Trailing-90-day PNRs → the AVERAGE MONTH inside that window.
 *
 * 90 / (365/12) months, not 3 — a 90-day window is 2.96 average months, and
 * dividing by three understates every monthly figure by 1.4%. On a bar somebody
 * is paid against, that is the wrong side of the line for whoever is sitting on
 * it.
 *
 * AN AVERAGE, NOT A FORECAST, and the distinction is the reason this is not an
 * annualization. Nothing here extrapolates past the measured window: it reports
 * what a typical month looked like over the last 90 days. It still cannot tell
 * you about the month a bonus is actually paid on — a strong month and a weak
 * one average to a figure neither of them was, and travel is seasonal — so the
 * UI says "average month" wherever this appears.
 */
export function monthlyPnrs(pnrs90: number): number {
  return Math.round(pnrs90 / (PNR_WINDOW_DAYS / DAYS_PER_MONTH));
}

/**
 * PNRs across one book, and how much of that book could not be measured.
 *
 * `unmeasured` is not a diagnostic afterthought: a book where half the customers
 * carry no PNR data produces a total that is arithmetically correct and
 * materially misleading, and the only defence is reporting both numbers together.
 */
export function pnrsOfBook(
  accounts: readonly Account[],
  pnrs90: Readonly<Record<string, number>> | undefined,
): { pnrs90: number; measured: number; unmeasured: number } {
  let total = 0;
  let measured = 0;
  let unmeasured = 0;
  for (const account of accounts) {
    const value = pnrs90?.[account.id];
    // `undefined` is "no data recorded"; a recorded 0 is a real customer who
    // booked nothing this quarter and counts as measured.
    if (typeof value === 'number' && Number.isFinite(value)) {
      total += value;
      measured += 1;
    } else {
      unmeasured += 1;
    }
  }
  return { pnrs90: total, measured, unmeasured };
}

/** One person, and what their whole book books. */
export interface CsmPnrs {
  csmKey: string;
  displayName: string;
  /** Trailing 90 days across their whole book — every tier they staff. */
  pnrs90: number;
  /** The average month inside that window. See monthlyPnrs. */
  monthly: number;
  measured: number;
  unmeasured: number;
  /**
   * How many tiers this person staffs.
   *
   * More than one used to mean they appeared under several bars with no rule
   * saying which governed. It is now decided (Jeff, 2026-08-29): the bar is the
   * one for the tier they are ASSIGNED TO on the Team tab. So this is no longer
   * an ambiguity — it is context, worth showing because someone measured against
   * Medium's bar while also working Small accounts is a fact a reader should see.
   */
  tiersStaffed: number;
  /** The other tiers they hold seats in, beyond the one they are measured under. */
  alsoStaffs: string[];
}

export interface TierIncentiveRow {
  tierKey: string;
  label: string;
  /** Monthly PNRs per CSM. `null` means no bar has been set — not a bar of zero. */
  threshold: number | null;
  /** The people staffing this tier, with their whole book. */
  staff: CsmPnrs[];
  /** How many of them clear the bar. Meaningless, and zero, when it is unset. */
  clears: number;
  /** The tier's OWN book — the accounts that tier into it, whoever holds them. */
  bookPnrs90: number;
  bookMonthly: number;
  bookAccounts: number;
  bookUnmeasured: number;
}

/**
 * The tier each person is MEASURED UNDER, keyed by the slot's `csmId`.
 *
 * Someone can hold seats in more than one tier, and each tier has its own bar,
 * so something has to say which one governs their bonus. It is the tier they are
 * assigned to on the Team tab (Jeff, 2026-08-29) — the one Team's own selector
 * shows — which makes Team the single place a person's tier is decided rather
 * than a question with two answers depending on which screen you opened.
 *
 * "First seat in the slots list" IS that rule, because it is exactly what Team's
 * selector renders. Defined here, once, so the two cannot drift: if Team ever
 * shows a different seat, this must move with it or a CSM would be measured
 * against a bar the screen does not show them under.
 */
export function primaryTierByCsmId(
  slots: readonly SlotDto[],
): Map<string, string> {
  const out = new Map<string, string>();
  for (const slot of slots) {
    // A vacancy is a planned hire, not a person, and has no bonus to earn.
    if (slot.csmId === null) continue;
    if (!out.has(slot.csmId)) out.set(slot.csmId, slot.tierKey);
  }
  return out;
}

/**
 * Each tier's bar, and the people measured against it.
 *
 * EACH PERSON APPEARS EXACTLY ONCE, under the tier they are assigned to on the
 * Team tab — see `primaryTierByCsmId`. Listing a multi-tier CSM under every bar
 * they hold a seat in was the honest thing to do while the rule was undecided,
 * but it left a reader unable to answer "does this person get paid": two bars,
 * two different answers, no tiebreak.
 *
 * A PERSON IS MEASURED ON THEIR WHOLE BOOK, not on the slice of it that tiers
 * into the tier whose bar is being shown. That is deliberate and it is the
 * difference between a plan you could pay out and a statistic: a CSM books the
 * transactions they book, and a bonus is earned on all of them. The tier's own
 * book is reported separately, because "what this tier generates" is a different
 * and also useful question — it is the one that says whether a bar is reachable
 * at all once the seats change.
 *
 * VACANCIES ARE NOT PEOPLE and are excluded: a planned hire books nothing, and
 * counting them would drag every average toward zero and make a reachable bar
 * read as impossible.
 */
export function tierIncentiveRows(input: {
  tiers: readonly StoredTierDto[];
  slots: readonly SlotDto[];
  /** Slot `csmId` (a DB row id) → engine key. See lib/model's rosterKeyIndex. */
  keyById: ReadonlyMap<string, string>;
  /** Engine key → the computed book, as the pages already build it. */
  bookOf: ReadonlyMap<string, { accounts: Account[] }>;
  byTier: Readonly<Record<string, Account[]>>;
  pnrs90: Readonly<Record<string, number>> | undefined;
  displayName: (key: string) => string;
}): TierIncentiveRow[] {
  const { tiers, slots, keyById, bookOf, byTier, pnrs90, displayName } = input;

  // Every tier each person holds a seat in, so a row can say what else they
  // work beyond the tier whose bar governs them.
  const tiersOf = new Map<string, Set<string>>();
  for (const slot of slots) {
    if (slot.csmId === null) continue;
    const key = keyById.get(slot.csmId);
    if (key === undefined) continue;
    if (!tiersOf.has(key)) tiersOf.set(key, new Set());
    tiersOf.get(key)!.add(slot.tierKey);
  }
  const primaryOf = primaryTierByCsmId(slots);

  return tiers.map((tier) => {
    // DISTINCT people, not seats, and ONLY the people this tier governs. Two
    // seats in one tier is how "most of their time is here" is expressed; it is
    // still one person clearing one bar, and counting seats would double them.
    const keys: string[] = [];
    for (const slot of slots) {
      if (slot.csmId === null) continue;
      if (primaryOf.get(slot.csmId) !== tier.key) continue;
      const key = keyById.get(slot.csmId);
      if (key !== undefined && !keys.includes(key)) keys.push(key);
    }

    const staff: CsmPnrs[] = keys.map((key) => {
      const book = pnrsOfBook(bookOf.get(key)?.accounts ?? [], pnrs90);
      return {
        csmKey: key,
        displayName: displayName(key),
        pnrs90: book.pnrs90,
        monthly: monthlyPnrs(book.pnrs90),
        measured: book.measured,
        unmeasured: book.unmeasured,
        tiersStaffed: tiersOf.get(key)?.size ?? 0,
        alsoStaffs: [...(tiersOf.get(key) ?? [])]
          .filter((t) => t !== tier.key)
          .sort(),
      };
    });

    const tierBook = pnrsOfBook(byTier[tier.key] ?? [], pnrs90);
    const threshold = tier.incentivePnrThreshold ?? null;

    return {
      tierKey: tier.key,
      label: tier.label,
      threshold,
      staff,
      // An UNSET bar is cleared by nobody, rather than by everybody. Zero here
      // is not "they all failed" — the verdict below never reports a count
      // against a bar that does not exist.
      clears:
        threshold === null
          ? 0
          : staff.filter((s) => s.monthly >= threshold).length,
      bookPnrs90: tierBook.pnrs90,
      bookMonthly: monthlyPnrs(tierBook.pnrs90),
      bookAccounts: (byTier[tier.key] ?? []).length,
      bookUnmeasured: tierBook.unmeasured,
    };
  });
}

/**
 * What a tier row actually says, as a closed set of cases.
 *
 * A discriminated union rather than a formatted string, so the four states that
 * are NOT a pass/fail — no bar set, nobody staffing it, nothing measured — have
 * to be handled at the call site instead of collapsing into "0 of 0 clear",
 * which reads as a failing plan when it is an unwritten one.
 */
export type TierVerdict =
  | { kind: 'no-threshold' }
  | { kind: 'unstaffed'; threshold: number }
  | { kind: 'unmeasured'; threshold: number }
  | { kind: 'all-clear'; threshold: number; people: number }
  | { kind: 'some-clear'; threshold: number; clears: number; people: number }
  | { kind: 'none-clear'; threshold: number; people: number; shortest: number };

export function tierVerdict(row: TierIncentiveRow): TierVerdict {
  if (row.threshold === null) return { kind: 'no-threshold' };
  const threshold = row.threshold;
  if (row.staff.length === 0) return { kind: 'unstaffed', threshold };
  // Nobody has a single measured customer: the comparison has no input at all,
  // and reporting "0 of 3 clear" would blame the team for missing data.
  if (row.staff.every((s) => s.measured === 0))
    return { kind: 'unmeasured', threshold };

  const people = row.staff.length;
  if (row.clears === people) return { kind: 'all-clear', threshold, people };
  if (row.clears > 0)
    return { kind: 'some-clear', threshold, clears: row.clears, people };
  return {
    kind: 'none-clear',
    threshold,
    people,
    shortest: Math.max(...row.staff.map((s) => threshold - s.monthly)),
  };
}

/**
 * Whether a typed threshold is one cm-api would accept, in the client's words.
 *
 * MIRRORS the rule in cm-api's `validateStaffingConfig` — whole number, zero or
 * more, or null for unset. Restated rather than imported for the usual reason
 * (cm-api is npm-standalone), and checked here FIRST because a config version is
 * immutable: a rejected publish is recoverable, but this saves a round trip that
 * ends in a wall of server problems for a typo.
 */
export function thresholdProblem(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null; // unset, which is a legitimate state
  const value = Number(trimmed);
  if (!Number.isFinite(value)) return 'must be a number';
  if (value < 0) return 'cannot be negative';
  if (!Number.isInteger(value)) return 'must be a whole number of PNRs';
  return null;
}

/** A typed threshold as it will be stored. Empty means unset, which is null. */
export function parseThreshold(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}

/** PNR counts, formatted. Whole numbers with separators — never a currency. */
export function pnrs(n: number): string {
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}
